import { API_BASE_URL, API_TIMEOUT_MS } from '../config/env';
import { appVersion } from './appVersion';
import { ApiError, ApiResponse } from './types';

/**
 * The only place in the app that calls `fetch`.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT TAKES OFF EVERY SERVICE FUNCTION'S HANDS
 * ---------------------------------------------------------------------------
 *   the base URL          so no screen ever spells out a host
 *   the auth header       attached from the token this module holds
 *   the timeout           `fetch` has none, so a dead network hangs forever
 *   the envelope          callers get `data`, not `{ success, data, ... }`
 *   the failures          everything throws `ApiError` - see `types.ts`
 *
 * A service function above this is therefore four lines and no error handling:
 *
 *   export const list = () => api.get<Booking[]>('/bookings');
 *
 * ---------------------------------------------------------------------------
 * THE TOKEN LIVES IN THIS MODULE, NOT IN REACT STATE
 * ---------------------------------------------------------------------------
 * `setAccessToken` is called from two places - the sign-in flow, and the
 * rehydrate on launch - and read on every request. Putting it in a context
 * instead would mean every service function needed a hook to reach it, which
 * makes calling the API from outside a component impossible.
 *
 * It is memory only. Persisting it is `src/data/storage.ts`'s job, and the two
 * are deliberately separate: this module is what the network layer uses, that
 * one is what survives a restart.
 */

let accessToken: string | null = null;

/**
 * A callback for "the server rejected our token".
 *
 * Registered once by whatever owns the session. Without it, a 401 on a
 * background request would leave the app showing a signed-in shell that
 * cannot load anything - the state the customer reads as "the app is broken"
 * rather than "I need to sign in again".
 *
 * This is the LAST resort now, not the first response to a 401 - see
 * `refreshHandler` below. It still exists for the case a refresh cannot fix:
 * no refresh token on file, or the refresh token itself has expired (the
 * access token's 15 minutes is nothing next to its 7 days, but 7 days does
 * eventually run out).
 */
let onUnauthorized: (() => void) | null = null;

/**
 * A callback for "go get a new access token".
 *
 * Registered by whatever owns the session, same as `onUnauthorized` - it is
 * not `services/auth.ts`'s `refresh()` called directly, because that would
 * make this module import the one that already imports IT (`auth.ts` calls
 * `api.post` for every request it makes, this one included), and because the
 * refresh token itself lives in React state, not here. The handler is
 * expected to call `refresh()`, update `accessToken` via `setAccessToken`
 * (and its own persisted copy) on success, and resolve to whether it worked.
 *
 * ---------------------------------------------------------------------------
 * WHY A 401 USED TO MEAN AN IMMEDIATE SIGN-OUT
 * ---------------------------------------------------------------------------
 * Nothing here ever called `refresh()` - not on a 401, not on a timer, not on
 * relaunch. The access token is deliberately short-lived, 15 minutes, on the
 * understanding that something would renew it quietly in the background. With
 * nothing doing that, every 401 - the token had simply expired mid-session, or
 * the app was reopened after being backgrounded - went straight to
 * `onUnauthorized`, which signed the customer all the way out and discarded a
 * refresh token that was still good for up to 7 days. "Login again and again"
 * was that 15-minute expiry, not a real security event.
 */
/**
 * How a refresh ended.
 *
 * `true` - there is a new token, retry. `false` - the server refused the
 * refresh token, the session is over. `'unavailable'` - the refresh could not
 * be completed (no signal, timeout, server restarting): nothing is known about
 * the token, so the person must NOT be signed out.
 */
export type RefreshOutcome = boolean | 'unavailable';

let refreshHandler: (() => Promise<RefreshOutcome>) | null = null;

/**
 * At most one refresh in flight at a time.
 *
 * A page that fires several requests at once - Home does, on launch - can get
 * several 401s back together if the token expired while they were in the air.
 * Without this, each one would call the handler separately: three network
 * round trips to `/auth/refresh` for what is the same stale token, and a real
 * chance the second attempt uses a refresh token the first one has already
 * rotated out from under it. Every concurrent 401 instead awaits the same
 * promise and shares its one outcome.
 */
let refreshing: Promise<RefreshOutcome> | null = null;

const attemptRefresh = (): Promise<RefreshOutcome> => {
  if (!refreshing) {
    refreshing = (refreshHandler?.() ?? Promise.resolve(false))
      // A handler that threw is not evidence the token is dead.
      .catch((): RefreshOutcome => 'unavailable')
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
};

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};

export const getAccessToken = (): string | null => accessToken;

export const setUnauthorizedHandler = (handler: (() => void) | null): void => {
  onUnauthorized = handler;
};

export const setRefreshHandler = (
  handler: (() => Promise<RefreshOutcome>) | null,
): void => {
  refreshHandler = handler;
};

type Options = {
  /** Query string values. `undefined` entries are dropped, not sent as "undefined". */
  query?: Record<string, string | number | boolean | undefined>;
  /** JSON body. Anything but GET. */
  body?: unknown;
  /**
   * Send without the bearer header even when signed in.
   *
   * For the auth endpoints themselves: presenting an expired token to
   * `/auth/verify` would trip the 401 handler and sign the user out in the
   * middle of signing them in.
   */
  anonymous?: boolean;
  signal?: AbortSignal;
  /**
   * Extra headers for the few endpoints that take one.
   *
   * `Idempotency-Key` is the reason this exists: a retried booking must return
   * the original rather than create a second one, and the key has to travel
   * beside the body rather than in it.
   */
  headers?: Record<string, string>;
};

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  { query, body, anonymous, signal, headers }: Options = {},
  /**
   * Internal only - not part of `Options`, so no caller ever passes this.
   * Set on the one recursive call this function makes to itself, after a
   * successful refresh, so that call's own 401 (the refresh did not help,
   * or the retried request fails for an unrelated reason) goes straight to
   * `onUnauthorized` rather than looping.
   */
  retried = false,
): Promise<T> {
  const url = API_BASE_URL + path + buildQuery(query);

  /*
   * The timeout, as an abort.
   *
   * `fetch` has no timeout option and never gives up on its own. On a phone
   * that has walked out of Wi-Fi range the request simply never settles - no
   * error, no rejection, a spinner until the app is killed.
   */
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

  // The caller's own abort (a screen unmounting) has to reach the same
  // controller, or unmounting mid-request leaves it running.
  const external = signal;
  const forward = () => controller.abort();
  external?.addEventListener('abort', forward);

  let response: Response;

  try {
    response = await fetch(url, {
      method,
      /*
       * React Native ships its own `AbortSignal` type, structurally slightly
       * different from the DOM one `AbortController` is typed against - the
       * `onabort` handler is nullable in one and not the other. They are the
       * same object at runtime; only the two `@types` packages disagree.
       */
      signal: controller.signal as unknown as RequestInit['signal'],
      headers: {
        Accept: 'application/json',
        /*
         * On every request, not just the version check.
         *
         * The backend's server-driven UI already reads this to decide which
         * config a build may be shown, and the release gate reads it as a
         * fallback when the query parameter is absent. Sending it once here
         * means neither has to be threaded through individual call sites.
         */
        'X-App-Version': appVersion(),
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(accessToken && !anonymous
          ? { Authorization: `Bearer ${accessToken}` }
          : {}),
        ...headers,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch (thrown) {
    // Aborted, refused, no DNS, no signal - all indistinguishable here, and
    // all the same thing to the customer: it did not reach the server.
    const aborted =
      thrown instanceof Error &&
      (thrown.name === 'AbortError' || external?.aborted);

    throw new ApiError(
      aborted
        ? 'That took too long. Check your connection and try again.'
        : "Couldn't reach Homingo. Check your connection and try again.",
      0,
      [],
      true,
    );
  } finally {
    clearTimeout(timer);
    external?.removeEventListener('abort', forward);
  }

  const payload = await readEnvelope<T>(response);

  if (!response.ok || !payload?.success) {
    const error = new ApiError(
      messageFor(payload, response.status),
      response.status,
      payload?.errors ?? [],
    );

    // Announced before it is thrown, so the session is torn down even if the
    // caller swallows the error.
    if (error.isUnauthorized && !anonymous) {
      /*
       * One refresh, one retry - not an immediate sign-out. See the note on
       * `refreshHandler`. `retried` stops this from looping: the retried
       * call passes `true`, so if IT also comes back 401 (the refresh token
       * has expired too, or the retry failed for some unrelated reason),
       * this falls straight through to `onUnauthorized` below instead of
       * refreshing again.
       */
      if (!retried && refreshHandler) {
        const refreshed = await attemptRefresh();
        if (refreshed === true) {
          return request<T>(
            method,
            path,
            { query, body, anonymous, signal, headers },
            true,
          );
        }
        // Could not reach the server to ask. The token may be perfectly good,
        // so this request fails on its own and the session is left alone.
        if (refreshed === 'unavailable') throw error;
      }
      onUnauthorized?.();
    }

    throw error;
  }

  // `data` is null on endpoints that return nothing. `as T` is honest there:
  // the caller declared `void` and will not read it.
  return payload.data as T;
}

/**
 * Parses the body, tolerating one that is not the envelope.
 *
 * A proxy, a tunnel or a 502 page returns HTML, and `response.json()` throws
 * on it - which would surface as "Unexpected token < in JSON" instead of
 * something a customer can act on.
 */
async function readEnvelope<T>(
  response: Response,
): Promise<ApiResponse<T> | null> {
  try {
    return (await response.json()) as ApiResponse<T>;
  } catch {
    return null;
  }
}

/**
 * The most useful sentence the failure has to offer.
 *
 * The server answers a rejected body with `message: "Validation failed"` and
 * puts what actually went wrong in `errors` - one entry per broken rule. Shown
 * as-is, the customer reads "Validation failed", which tells them nothing and
 * tells whoever they call even less. So a generic envelope defers to its first
 * detail, and anything more specific is left exactly as the server wrote it.
 */
const GENERIC_MESSAGES = ['Validation failed', 'Internal server error'];

function messageFor<T>(payload: ApiResponse<T> | null, status: number): string {
  const detail = payload?.errors?.find(entry => entry.message)?.message;

  if (payload?.message && !GENERIC_MESSAGES.includes(payload.message)) {
    return payload.message;
  }

  return detail ?? payload?.message ?? fallbackMessage(status);
}

const fallbackMessage = (status: number): string => {
  if (status >= 500) return 'Homingo is having trouble. Please try again.';
  if (status === 404) return 'That is no longer available.';
  return 'Something went wrong. Please try again.';
};

const buildQuery = (
  query: Record<string, string | number | boolean | undefined> | undefined,
): string => {
  if (!query) return '';

  const pairs = Object.entries(query)
    .filter(([, value]) => value !== undefined && value !== '')
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    );

  return pairs.length ? `?${pairs.join('&')}` : '';
};

export const api = {
  get: <T>(path: string, options?: Omit<Options, 'body'>) =>
    request<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options?: Omit<Options, 'body'>) =>
    request<T>('POST', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<Options, 'body'>) =>
    request<T>('PATCH', path, { ...options, body }),
  put: <T>(path: string, body?: unknown, options?: Omit<Options, 'body'>) =>
    request<T>('PUT', path, { ...options, body }),
  delete: <T>(path: string, options?: Omit<Options, 'body'>) =>
    request<T>('DELETE', path, options),
};
